import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  handleSelectAts,
  handleSelectTC,
  selectForceAndCommands,
} from '../../store/slices/settings/forceAndCommandsSlice';
import {
  bulkUpdateBlowerDeviceService,
  bulkUpdateSwitchDeviceService,
} from '../../../services';
import { selectedSpecDevices } from '../../../helpers/setting';
import { logger } from '../../../helpers/logger';
import {
  atsPayload,
  thermocoupleChannelWrites,
} from '../../../helpers/payloads/settingsPayloads';
import { SETTINGS_HEADERS } from '../settingsHeaders';
import { saveFailedMessage, settingsMessage } from './settingsMessage';
import { sendSettingWrites } from './sendSettingWrites';

const noAts = () => [false, false, false];

const noTcChannels = () => ({
  outsideTemp: false,
  burningChamber: false,
  encloseTemp: false,
  currEss: false,
  currTgs: false,
  currTes: false,
});

// The ATS rows: which spec holds their devices and how the option is sent.
const ATS_SYSTEMS = {
  [SETTINGS_HEADERS.ESS]: {
    swt: 'ess',
    spec: 'essSpec',
    send: (deviceIds, body) => bulkUpdateSwitchDeviceService(deviceIds, body),
  },
  [SETTINGS_HEADERS.TGS]: {
    swt: 'tgs',
    spec: 'tgsSpec',
    send: (deviceIds, body) =>
      bulkUpdateBlowerDeviceService(deviceIds, 'TGS', body),
  },
  [SETTINGS_HEADERS.TES]: {
    swt: 'tes',
    spec: 'tesSpec',
    send: (deviceIds, body) =>
      bulkUpdateBlowerDeviceService(deviceIds, 'TES', body),
  },
};

/**
 * What a row's ATS option reads as in the settings slice. TES has three
 * options: switch to TGS, reactivate, remain off.
 */
export const atsSelectionLabel = (swt, selected, header) => {
  if (swt === 'tes' && selected[header] === 0) {
    return 'switch';
  }
  if (swt === 'tes') {
    return selected[header] === 1 ? 'reactivate' : 'block';
  }
  return selected[header] === 0 ? 'reactivate' : 'block';
};

const withFlag = (flags, index, value) =>
  flags.map((flag, idx) => (idx === index ? value : flag));

const atsMessage = (swt, text) =>
  settingsMessage('force&commands', `${swt}-change options`, [text]);

const tcMessage = (text) =>
  settingsMessage('force&commands', 'system commands-change options', [text]);

/**
 * The force & commands panel: an ATS option per system row and the
 * system-commands t/c channels. `save(header)` sends the row's choice and
 * resolves to the message to show.
 */
export const useSaveForceAndCommands = () => {
  const dispatch = useDispatch();
  const forceAndCommands = useSelector(selectForceAndCommands);

  const [selectAtsButtonsState, setSelectAtsButtonsState] = useState(noAts);
  const [selected, setSelected] = useState([null, null, null]);
  const [activeSelect, setActiveSelect] = useState(null);
  const [tCNumber, setTCNumber] = useState([0, 0, 0, 0, 0, 0]);
  const [isSave, setIsSave] = useState(noTcChannels);
  const [isAppliedAtsButtonState, setIsAppliedAtsButtonState] =
    useState(noAts);

  const saveAts = async (header) => {
    const { swt, spec, send } = ATS_SYSTEMS[header];
    if (!selectAtsButtonsState[header]) {
      return atsMessage(swt, 'please select ats options before apply.');
    }

    const devices = selectedSpecDevices(forceAndCommands[spec], 'isSelected');
    if (devices.length === 0) {
      setSelectAtsButtonsState(noAts());
      return atsMessage(swt, 'please select switches to continue ats.');
    }

    try {
      await send(
        devices.map(({ machine }) => machine),
        atsPayload(selected[header]),
      );
    } catch (error) {
      logger.error('ATS save failed', error);
      return saveFailedMessage('force&commands', `${swt}-change options`, error, {
        asList: true,
      });
    }

    const selection = atsSelectionLabel(swt, selected, header);
    devices.forEach(({ location, specificLocation, machine }) =>
      dispatch(
        handleSelectAts({ swt, location, specificLocation, machine, selection }),
      ),
    );
    setSelectAtsButtonsState(noAts());
    setIsAppliedAtsButtonState((flags) => withFlag(flags, header, true));
    return atsMessage(swt, 'settings have been applied.');
  };

  const saveThermocouples = async () => {
    const { writes, selections, channels, devicesCount } =
      thermocoupleChannelWrites({
        sysSpec: forceAndCommands.sysSpec,
        isSave,
        tCNumber,
      });
    try {
      await sendSettingWrites(writes);
    } catch (error) {
      logger.error('t/c channel save failed', error);
      return saveFailedMessage(
        'force&commands',
        'system commands-change options',
        error,
        { asList: true },
      );
    }
    selections.forEach((selection) => dispatch(handleSelectTC(selection)));
    setIsSave(noTcChannels());

    if ((activeSelect && tCNumber[0] <= 0) || channels === 0) {
      return tcMessage('please select a t/c option before apply.');
    }
    if (devicesCount === 0) {
      return tcMessage('please select switches to continue t/c telemetry.');
    }
    return tcMessage('settings have been applied.');
  };

  const save = async (header) => {
    if (ATS_SYSTEMS[header]) {
      return saveAts(header);
    }
    if (header === SETTINGS_HEADERS.SYSTEM_COMMANDS) {
      return saveThermocouples();
    }
    setIsSave(noTcChannels());
    return settingsMessage('', '', '', '');
  };

  const cancel = (header) =>
    setIsAppliedAtsButtonState((flags) => withFlag(flags, header, false));

  return {
    panelProps: {
      buttonsState: selectAtsButtonsState,
      setButtonsState: setSelectAtsButtonsState,
      selected,
      setSelected,
      activeSelect,
      setActiveSelect,
      tCNumber,
      setTCNumber,
      isSave,
      setIsSave,
      isAppliedAtsButtonState,
      setIsAppliedAtsButtonState,
    },
    save,
    cancel,
  };
};
